import React, { useState, useRef, useEffect } from "react";
import { db } from "../FirebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import axios from "axios";
import { FaUpload, FaTimes, FaSpinner } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import * as THREE from 'three';

// ⚠️ IMPORTANT: Replace these with your actual Cloudinary credentials
const CLOUDINARY_UPLOAD_PRESET = "ml_default";
const CLOUDINARY_CLOUD_NAME = "djj74c0oj"; // Assuming your cloud name

const setupThreeJS = (canvasRef) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const sizes = {
    width: window.innerWidth,
    height: document.body.scrollHeight
  };

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 1000);
  camera.position.z = 50;

  const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true,
    antialias: true
  });
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const particleCount = 1500;
  const positions = new Float32Array(particleCount * 3);
  const velocities = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 200;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 200;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 200;

    velocities[i * 3] = (Math.random() - 0.5) * 0.02;
    velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.02;
    velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.02;
  }

  const particleGeometry = new THREE.BufferGeometry();
  particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  
  const particleMaterial = new THREE.PointsMaterial({
    color: 0x4ADE80,
    size: 0.3,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.8
  });
  
  const particles = new THREE.Points(particleGeometry, particleMaterial);
  scene.add(particles);

  const clock = new THREE.Clock();
  const animate = () => {
    const elapsedTime = clock.getElapsedTime();

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] += velocities[i * 3] * Math.sin(elapsedTime * 0.1);
      positions[i * 3 + 1] += velocities[i * 3 + 1] * Math.cos(elapsedTime * 0.1);
      positions[i * 3 + 2] += velocities[i * 3 + 2] * Math.sin(elapsedTime * 0.1);

      if (Math.abs(positions[i * 3]) > 100) velocities[i * 3] *= -1;
      if (Math.abs(positions[i * 3 + 1]) > 100) velocities[i * 3 + 1] *= -1;
      if (Math.abs(positions[i * 3 + 2]) > 100) velocities[i * 3 + 2] *= -1;
    }
    
    particleGeometry.attributes.position.needsUpdate = true;

    particles.rotation.y = elapsedTime * 0.005;
    
    renderer.render(scene, camera);
    requestAnimationFrame(animate);
  };

  const onWindowResize = () => {
    sizes.width = window.innerWidth;
    sizes.height = document.body.scrollHeight;
    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();
    renderer.setSize(sizes.width, sizes.height);
  };
  window.addEventListener('resize', onWindowResize);

  animate();

  return () => {
    window.removeEventListener('resize', onWindowResize);
    renderer.dispose();
  };
};

export default function AddFoodForm() {
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    prepTime: "",
    whatsapp: "",
    location: "",
    category: "",
    university: "",
    dietary: [],
    image: null
  });

  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const cleanup = setupThreeJS(canvasRef);
    return cleanup;
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleDietaryChange = (tag) => {
    setForm(prev => ({
      ...prev,
      dietary: prev.dietary.includes(tag)
        ? prev.dietary.filter(t => t !== tag)
        : [...prev.dietary, tag]
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm(prev => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setForm(prev => ({ ...prev, image: null }));
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const requiredFields = ['name', 'description', 'price', 'prepTime', 'whatsapp', 'location', 'category', 'university'];
    const missing = requiredFields.filter(field => !form[field].trim());
    if (missing.length > 0 || !form.image) {
      toast.error(`Please complete all fields and upload an image. Missing: ${missing.join(', ')}${!form.image ? ', image' : ''}`);
      setLoading(false);
      return;
    }

    try {
      const data = new FormData();
      data.append("file", form.image);
      data.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

      toast.info("Uploading image...", { autoClose: 3000 });
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        data
      );
      const imageUrl = res.data.secure_url;
      const publicId = res.data.public_id;

      await addDoc(collection(db, "foodItems"), {
        name: form.name,
        description: form.description,
        price: parseFloat(form.price),
        prepTime: form.prepTime,
        whatsapp: form.whatsapp,
        location: form.location,
        university: form.university,
        category: form.category,
        dietary: form.dietary,
        imageUrl,
        public_id: publicId,
        createdAt: serverTimestamp(),
      });

      toast.success("Menu item added successfully!");

      setForm({
        name: "",
        description: "",
        price: "",
        prepTime: "",
        whatsapp: "",
        location: "",
        category: "",
        university: "",
        dietary: [],
        image: null
      });
      setImagePreview(null);

    } catch (err) {
      console.error("Error adding menu item:", err);
      toast.error("Error adding menu item.");
    }

    setLoading(false);
  };

  return (
    <div className="bg-transparent min-h-screen flex items-center justify-center p-4 md:p-8 font-['Space_Grotesk'] relative overflow-hidden">
      <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full z-0 pointer-events-none" />
      <ToastContainer position="bottom-right" />
      
      <motion.form 
        onSubmit={handleSubmit}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative max-w-2xl w-full mx-auto bg-white/5 backdrop-blur-xl p-8 rounded-3xl shadow-2xl space-y-8 border border-white/10 z-10"
      >
        <motion.h2
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-lime-300 text-center mb-8 drop-shadow-lg"
        >
          Add Menu Delight
        </motion.h2>

        {/* Basic Info Section */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Dish Name *</label>
            <input 
              name="name" 
              placeholder="e.g., Signature Sushi Roll" 
              value={form.name}
              onChange={handleChange} 
              className="w-full bg-white/5 p-4 rounded-xl border border-white/20 text-white placeholder-gray-500 focus:border-emerald-400 focus:outline-none transition-all shadow-inner"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description *</label>
            <textarea 
              name="description" 
              placeholder="Describe your culinary creation..." 
              value={form.description}
              onChange={handleChange} 
              className="w-full bg-white/5 p-4 rounded-xl border border-white/20 text-white placeholder-gray-500 focus:border-emerald-400 focus:outline-none transition-all shadow-inner h-32"
              required
            />
          </div>
        </div>

        {/* Pricing & Prep Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Price (₦) *</label>
            <input 
              type="number"
              name="price" 
              placeholder="e.g., 2500" 
              value={form.price}
              onChange={handleChange} 
              className="w-full bg-white/5 p-4 rounded-xl border border-white/20 text-white placeholder-gray-500 focus:border-emerald-400 focus:outline-none transition-all shadow-inner"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Preparation Time *</label>
            <input 
              name="prepTime" 
              placeholder="e.g., 30 minutes" 
              value={form.prepTime}
              onChange={handleChange} 
              className="w-full bg-white/5 p-4 rounded-xl border border-white/20 text-white placeholder-gray-500 focus:border-emerald-400 focus:outline-none transition-all shadow-inner"
              required
            />
          </div>
        </div>

        {/* Contact & Location */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">WhatsApp Number *</label>
            <input 
              type="tel"
              name="whatsapp" 
              placeholder="e.g., +2348012345678" 
              value={form.whatsapp}
              onChange={handleChange} 
              className="w-full bg-white/5 p-4 rounded-xl border border-white/20 text-white placeholder-gray-500 focus:border-emerald-400 focus:outline-none transition-all shadow-inner"
              required
            />
          </div>

                  <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Location *</label>
            <input 
              name="university" 
              placeholder="univserity in full" 
              value={form.university}
              onChange={handleChange} 
              className="w-full bg-white/5 p-4 rounded-xl border border-white/20 text-white placeholder-gray-500 focus:border-emerald-400 focus:outline-none transition-all shadow-inner"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Location *</label>
            <input 
              name="location" 
              placeholder="e.g., Campus Kitchen Zone A" 
              value={form.location}
              onChange={handleChange} 
              className="w-full bg-white/5 p-4 rounded-xl border border-white/20 text-white placeholder-gray-500 focus:border-emerald-400 focus:outline-none transition-all shadow-inner"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Preparation Time *</label>
            <input 
              name="prepTime" 
              placeholder="e.g., 30 minutes" 
              value={form.prepTime}
              onChange={handleChange} 
              className="w-full bg-white/5 p-4 rounded-xl border border-white/20 text-white placeholder-gray-500 focus:border-emerald-400 focus:outline-none transition-all shadow-inner"
              required
            />
          </div>
        </div>

        {/* Category & Dietary */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
            <select 
              name="category" 
              value={form.category}
              onChange={handleChange}
              className="w-full bg-white/5 p-4 rounded-xl border border-white/20 text-black focus:border-emerald-400 focus:outline-none transition-all shadow-inner"
              required
            >
              <option value="">Select Category</option>
              <option value="appetizer">Appetizer</option>
              <option value="main">Main Course</option>
              <option value="dessert">Dessert</option>
              <option value="beverage">Beverage</option>
              <option value="snack">Snack</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Dietary Options</label>
            <div className="flex flex-wrap gap-2">
              {['Vegan', 'Vegetarian', 'Gluten-Free', 'Halal', 'Spicy'].map(tag => (
                <motion.button
                  key={tag}
                  type="button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleDietaryChange(tag)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    form.dietary.includes(tag) 
                      ? 'bg-emerald-600 text-white shadow-md' 
                      : 'bg-white/5 text-gray-300 border border-white/20 hover:bg-white/10'
                  }`}
                >
                  {tag}
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Image Upload */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300 mb-2">Dish Image *</label>
          <div className="relative border border-white/20 rounded-2xl p-6 bg-white/5 cursor-pointer hover:border-emerald-400/50 transition-all shadow-inner flex flex-col items-center justify-center min-h-[200px]">
            <input 
              type="file" 
              onChange={handleFileChange} 
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
              accept="image/*"
              required={!form.image}
            />
            {imagePreview ? (
              <div className="relative w-full h-48 rounded-xl overflow-hidden shadow-lg">
                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                <button 
                  type="button" 
                  onClick={handleRemoveImage} 
                  className="absolute top-2 right-2 bg-red-600/80 text-white p-1.5 rounded-full hover:bg-red-700 transition-all shadow-md"
                >
                  <FaTimes size={14} />
                </button>
              </div>
            ) : (
              <>
                <FaUpload className="text-4xl text-emerald-400 mb-2" />
                <p className="text-sm text-gray-400 font-medium">Upload high-quality photo</p>
              </>
            )}
          </div>
        </div>

        {/* Submit */}
        <motion.button 
          type="submit" 
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-gradient-to-r from-emerald-600 to-lime-600 hover:from-emerald-700 hover:to-lime-700 text-white font-bold py-4 rounded-2xl shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin" />
              <span>Adding Delight...</span>
            </>
          ) : (
            'Add to Menu'
          )}
        </motion.button>
      </motion.form>
    </div>
  );
}